/**
 * AI Chat Modal 组件
 * 聊天对话框
 */

import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Drawer, Input, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAIChatStore } from '@/store/useAIChatStore';
import { useUserStore } from '@/store/useUserStore';
import useIsMobile from '@/hooks/useIsMobile';
import ConfirmModal from '@/components/ConfirmModal';
import SvgIcon from '@/components/SvgIcon';
import MarkdownContent from '@/components/MarkdownContent';
import type { ContextType, Message } from '@/types/aiChat';
import { trackEvent } from '@/utils/analytics';
import { EVENTS } from '@/utils/analyticsEvents';
import { buildCoachBaseProps, buildCoachCoreProps, classifyCoachErrorType, getDurationSeconds, type AICoachErrorType } from '@/utils/aiCoachAnalytics';
import styles from './index.module.scss';

const { TextArea } = Input;
const COACH_SESSION_TIMEOUT_MS = 5 * 60 * 1000;
type SessionEndReason = 'closed' | 'timeout' | 'limit_reached';

const AIChatModal: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [retryState, setRetryState] = useState<{ message: string; errorType: AICoachErrorType } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const welcomeTimestampRef = useRef<string>('');
  const bodyScrollOverrideRef = useRef(false);
  const sessionContextRef = useRef<{ type: ContextType; id: number } | null>(null);
  const sessionStartMsRef = useRef(0);
  const sessionMessagesSentRef = useRef(0);
  const sessionStartedTrackedRef = useRef(false);
  const closeTrackedRef = useRef(false);
  const limitWarningTrackedRef = useRef(false);
  const limitReachedTrackedRef = useRef(false);
  const charLimitHitRef = useRef(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasModalOpenRef = useRef(false);
  const isMobile = useIsMobile();

  const { isModalOpen, setModalOpen, currentContext, quota, isLoading, sendMessage, loadHistory, loadQuota, getCurrentMessages, abortStream } = useAIChatStore();

  const { user } = useUserStore();
  const isUpgradeVariant = user?.subscription_plan === 'freemium';
  const limitModalTitle = t('aiChat.limitModal.title');
  const limitModalUpgradeDesc = t('aiChat.limitModal.freeDesc');
  const limitModalPaidDesc = t('aiChat.limitModal.paidDesc');
  const limitModalUpgradeText = t('aiChat.limitModal.upgrade');
  const limitModalCloseText = t('aiChat.limitModal.close');
  const limitModalCancelText = t('aiChat.limitModal.cancel');
  const inputLimit = 500;
  const coachBaseProps = currentContext ? buildCoachBaseProps(currentContext, user?.subscription_plan) : null;
  const coachCoreProps = currentContext ? buildCoachCoreProps(currentContext) : null;

  const trackSessionEnd = useCallback(
    (endReason: SessionEndReason) => {
      if (closeTrackedRef.current) return;
      const sessionContext = sessionContextRef.current;
      if (!sessionContext) return;
      const coreProps = buildCoachCoreProps(sessionContext);
      const baseProps = buildCoachBaseProps(sessionContext, user?.subscription_plan);
      const sessionDurationSeconds = getDurationSeconds(sessionStartMsRef.current);
      const messagesSent = sessionMessagesSentRef.current;

      if (endReason === 'closed') {
        trackEvent(EVENTS.AI_COACH_CLOSED, {
          ...coreProps,
          messages_sent: messagesSent,
          session_duration_seconds: sessionDurationSeconds,
        });
      }
      trackEvent(EVENTS.AI_COACH_SESSION_ENDED, {
        ...baseProps,
        messages_sent: messagesSent,
        session_duration_seconds: sessionDurationSeconds,
        end_reason: endReason,
      });
      closeTrackedRef.current = true;
    },
    [user?.subscription_plan]
  );

  const clearInactivityTimer = useCallback(() => {
    if (!inactivityTimerRef.current) return;
    clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = null;
  }, []);

  const closeByTimeout = useCallback(() => {
    trackSessionEnd('timeout');
    abortStream();
    setIsExpanded(false);
    setShowLimitModal(false);
    setModalOpen(false);
  }, [abortStream, setModalOpen, trackSessionEnd]);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    if (!isModalOpen) return;
    inactivityTimerRef.current = setTimeout(() => {
      closeByTimeout();
    }, COACH_SESSION_TIMEOUT_MS);
  }, [clearInactivityTimer, closeByTimeout, isModalOpen]);

  const openLimitModal = useCallback(() => {
    if (showLimitModal) return;
    if (coachBaseProps) {
      trackEvent(EVENTS.AI_COACH_LIMIT_MODAL_DISPLAYED, coachBaseProps);
    }
    setShowLimitModal(true);
  }, [coachBaseProps, showLimitModal]);

  // 加载聊天历史和配额
  useEffect(() => {
    if (isModalOpen && currentContext) {
      loadHistory();
      loadQuota();
    }
  }, [isModalOpen, currentContext, loadHistory, loadQuota]);

  useEffect(() => {
    return () => {
      abortStream();
      clearInactivityTimer();
    };
  }, [abortStream, clearInactivityTimer]);

  useEffect(() => {
    if (isModalOpen && currentContext) {
      welcomeTimestampRef.current = new Date().toISOString();
      sessionContextRef.current = { type: currentContext.type, id: currentContext.id };
      sessionStartMsRef.current = Date.now();
      sessionMessagesSentRef.current = 0;
      sessionStartedTrackedRef.current = false;
      closeTrackedRef.current = false;
      limitWarningTrackedRef.current = false;
      limitReachedTrackedRef.current = false;
      charLimitHitRef.current = false;
      setRetryState(null);
      setShowLimitModal(false);
    }
  }, [isModalOpen, currentContext]);

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      clearInactivityTimer();
    };
  }, [clearInactivityTimer, currentContext?.id, currentContext?.type, resetInactivityTimer]);

  useEffect(() => {
    if (wasModalOpenRef.current && !isModalOpen) {
      trackSessionEnd('closed');
    }
    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen, trackSessionEnd]);

  useEffect(() => {
    if (!isModalOpen) return;
    userScrolledUpRef.current = false;
    return undefined;
  }, [isModalOpen]);

  useEffect(() => {
    if (isMobile) return;
    const body = document.body;
    if (isModalOpen && !showLimitModal) {
      body.style.overflow = 'auto';
      body.style.paddingRight = '';
      bodyScrollOverrideRef.current = true;
      return;
    }
    if (bodyScrollOverrideRef.current) {
      body.style.overflow = '';
      body.style.paddingRight = '';
      bodyScrollOverrideRef.current = false;
    }
  }, [isModalOpen, showLimitModal, isMobile]);

  // 获取初始欢迎消息
  const getWelcomeMessage = (): string => {
    if (!currentContext) return t('aiChat.welcome.default');
    switch (currentContext.type) {
      case 'cv-analysis':
        return t('aiChat.welcome.cvAnalysis');
      case 'cl-analysis':
        return t('aiChat.welcome.clAnalysis');
      case 'cl-generation':
        return t('aiChat.welcome.clGeneration');
      case 'interview':
        return t('aiChat.welcome.interview');
      default:
        return t('aiChat.welcome.default');
    }
  };

  const getWelcomeMessageItem = (): Message | null => {
    if (!currentContext) return null;
    return {
      id: -1,
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: welcomeTimestampRef.current || new Date().toISOString(),
    };
  };

  const messages = getCurrentMessages();
  const welcomeMessage = getWelcomeMessageItem();
  const displayMessages = welcomeMessage ? [welcomeMessage, ...messages] : messages;

  // 自动滚动到底部
  useEffect(() => {
    if (!isModalOpen || userScrolledUpRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      return;
    }
    requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
    });
  }, [messages, isLoading, isModalOpen]);

  // 获取动态副标题
  const getSubtitle = () => {
    if (!currentContext) return '';
    switch (currentContext.type) {
      case 'cv-analysis':
        return t('aiChat.subtitle.cvAnalysis');
      case 'cl-analysis':
        return t('aiChat.subtitle.clAnalysis');
      case 'cl-generation':
        return t('aiChat.subtitle.clGeneration');
      case 'interview':
        return t('aiChat.subtitle.interview');
      default:
        return '';
    }
  };

  // 计算字符计数器状态
  const getCharacterCountStatus = () => {
    const charLength = inputValue.length;
    const limit = inputLimit;
    const percentage = (charLength / limit) * 100;

    return {
      show: percentage >= 80, // 恢复正常逻辑：80% 时显示
      isWarning: percentage >= 80 && percentage < 100,
      isError: percentage >= 100,
      count: charLength,
      limit,
    };
  };

  const charStatus = getCharacterCountStatus();

  // 检查是否达到配额限制
  const isQuotaReached = quota?.is_limit_reached || false;
  useEffect(() => {
    if (!isModalOpen || !quota || !coachBaseProps || !coachCoreProps) return;

    if (quota.limit > 0 && quota.used / quota.limit >= 0.8 && !quota.is_limit_reached && !limitWarningTrackedRef.current) {
      trackEvent(EVENTS.AI_COACH_LIMIT_WARNING_SHOWN, {
        ...coachBaseProps,
        messages_used: quota.used,
        messages_limit: quota.limit,
      });
      limitWarningTrackedRef.current = true;
    }

    if (quota.is_limit_reached && !limitReachedTrackedRef.current) {
      trackEvent(EVENTS.AI_COACH_LIMIT_REACHED, {
        ...coachBaseProps,
        messages_limit: quota.limit,
      });
      limitReachedTrackedRef.current = true;
    }
  }, [coachBaseProps, coachCoreProps, isModalOpen, quota]);

  const sendMessageWithAnalytics = useCallback(
    async (trimmedMessage: string, options?: { isRetry?: boolean; retryErrorType?: AICoachErrorType }) => {
      if (!coachBaseProps || !coachCoreProps || !trimmedMessage || isLoading || isQuotaReached || trimmedMessage.length > inputLimit) return;

      const messageNumber = messages.filter((message) => message.role === 'user').length + 1;
      if (options?.isRetry && options.retryErrorType) {
        trackEvent(EVENTS.AI_COACH_RETRY_CLICKED, {
          ...coachCoreProps,
          error_type: options.retryErrorType,
        });
      }
      if (!sessionStartedTrackedRef.current) {
        trackEvent(EVENTS.AI_COACH_SESSION_STARTED, {
          ...coachBaseProps,
          is_returning: messageNumber > 1,
        });
        sessionStartedTrackedRef.current = true;
      }
      trackEvent(EVENTS.AI_COACH_MESSAGE_SENT, {
        ...coachBaseProps,
        message_number: messageNumber,
        message_length: trimmedMessage.length,
        messages_remaining: quota ? Math.max(0, quota.remaining - 1) : undefined,
      });
      sessionMessagesSentRef.current += 1;
      resetInactivityTimer();
      setRetryState(null);

      const requestStart = performance.now();
      try {
        await sendMessage(trimmedMessage);
        trackEvent(EVENTS.AI_COACH_MESSAGE_RECEIVED, {
          ...coachCoreProps,
          message_number: messageNumber,
          response_time_ms: Math.max(0, Math.round(performance.now() - requestStart)),
        });
        resetInactivityTimer();
      } catch (error) {
        const errorType = classifyCoachErrorType(error);
        trackEvent(EVENTS.AI_COACH_ERROR, {
          ...coachCoreProps,
          error_type: errorType,
          message_number: messageNumber,
        });
        setRetryState({
          message: trimmedMessage,
          errorType,
        });
        console.error('Failed to send message:', error);
      }
    },
    [coachBaseProps, coachCoreProps, inputLimit, isLoading, isQuotaReached, messages, quota, resetInactivityTimer, sendMessage]
  );

  // 发送消息
  const handleSend = async () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage) return;
    setInputValue('');
    await sendMessageWithAnalytics(trimmedMessage);
  };

  const handleRetryClick = async () => {
    if (!retryState || isLoading) return;
    await sendMessageWithAnalytics(retryState.message, {
      isRetry: true,
      retryErrorType: retryState.errorType,
    });
  };

  // 处理输入框点击（配额限制时显示提示）
  const handleInputClick = () => {
    resetInactivityTimer();
    if (isQuotaReached) {
      openLimitModal();
    }
  };

  // 关闭对话框
  const handleClose = (endReason: SessionEndReason = 'closed') => {
    trackSessionEnd(endReason);
    clearInactivityTimer();
    abortStream();
    setIsExpanded(false);
    setShowLimitModal(false);
    setModalOpen(false);
  };

  // 处理配额限制确认
  const handleLimitConfirm = () => {
    if (coachCoreProps) {
      if (isUpgradeVariant) {
        trackEvent(EVENTS.AI_COACH_LIMIT_UPGRADE_CLICKED, coachCoreProps);
      } else {
        trackEvent(EVENTS.AI_COACH_LIMIT_NEW_ANALYSIS_CLICKED, coachCoreProps);
      }
    }
    setShowLimitModal(false);
    if (isUpgradeVariant) {
      // 跳转到订阅页面
      navigate('/subscription');
      handleClose('limit_reached');
    }
  };

  const handleLimitCancel = () => {
    if (coachBaseProps) {
      trackEvent(EVENTS.AI_COACH_LIMIT_MODAL_DISMISSED, coachBaseProps);
    }
    setShowLimitModal(false);
  };

  const handleInputChange = (value: string) => {
    if (coachBaseProps && value.length >= inputLimit && !charLimitHitRef.current) {
      trackEvent(EVENTS.AI_COACH_CHARACTER_LIMIT_HIT, {
        ...coachBaseProps,
        character_limit: inputLimit,
      });
      charLimitHitRef.current = true;
    } else if (value.length < inputLimit) {
      charLimitHitRef.current = false;
    }
    resetInactivityTimer();
    setInputValue(value);
  };

  // 渲染消息
  const renderMessage = (message: Message) => {
    const isUser = message.role === 'user';
    const time = new Date(message.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

    return (
      <div key={message.id} className={`${styles.message} ${isUser ? styles.userMessage : styles.aiMessage}`}>
        <div className={styles.messageWrapper}>
          <div className={styles.messageHeader}>
            <span className={styles.messageSender}>{isUser ? t('aiChat.youLabel') : t('aiChat.assistantLabel')}</span>
            <span className={styles.messageTime}>{time}</span>
          </div>
          <div className={styles.messageContent}>{isUser ? message.content : <MarkdownContent content={message.content} />}</div>
        </div>
      </div>
    );
  };

  const content = (
    <div className={`${styles.modalContent} ${isMobile ? styles.modalContentMobile : ''} ${isExpanded ? styles.modalContentExpanded : ''}`}>
      {/* 标题栏 */}
      {!isMobile && (
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h3 className={styles.title}>{t('aiChat.title')}</h3>
            <p className={styles.subtitle}>{getSubtitle()}</p>
          </div>
          <div className={styles.headerActions}>
            <button type="button" className={styles.actionButton} onClick={() => setIsExpanded((prev) => !prev)} aria-label={isExpanded ? 'Collapse chat window' : 'Expand chat window'}>
              <svg className={styles.actionIcon} aria-hidden="true">
                <use xlinkHref={isExpanded ? '#icon-close_fullscreen' : '#icon-open_in_full_'} fill="currentColor"></use>
              </svg>
            </button>
            <span className={styles.actionDivider} aria-hidden="true"></span>
            <button type="button" className={styles.actionButton} onClick={() => handleClose()} aria-label={t('common.close', { defaultValue: 'Close' })}>
              <svg className={styles.actionIcon} aria-hidden="true">
                <use xlinkHref="#icon-x-black" fill="currentColor"></use>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 消息列表 */}
      <div
        className={styles.messagesContainer}
        ref={messagesContainerRef}
        onScroll={() => {
          const container = messagesContainerRef.current;
          if (!container) return;
          const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
          userScrolledUpRef.current = distanceToBottom > 24;
        }}
      >
        {displayMessages.map(renderMessage)}
        <div ref={messagesEndRef} />
        {isLoading && (
          <div className={styles.loadingMessage}>
            <Spin size="small" />
            <span>{t('aiChat.thinking')}</span>
          </div>
        )}
        {retryState && (
          <div className={styles.retryBanner}>
            <span>{t('aiChat.retryHint', { defaultValue: 'Response failed. Please retry.' })}</span>
            <button type="button" className={styles.retryButton} onClick={handleRetryClick} disabled={isLoading} aria-label={t('common.retry', { defaultValue: 'Retry' })}>
              {t('common.retry', { defaultValue: 'Retry' })}
            </button>
          </div>
        )}
      </div>

      {/* 输入区域 */}
      <div className={styles.inputArea}>
        {/* 字符计数器 - 显示在输入框上方 */}
        {charStatus.show && (
          <div className={`${styles.charCounterAboveInput} ${charStatus.isError ? styles.charCounterError : ''}`}>
            {charStatus.count}/{charStatus.limit}
          </div>
        )}

        <div className={styles.inputWrapper}>
          <TextArea
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onClick={handleInputClick}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={t('aiChat.placeholder')}
            readOnly={isQuotaReached}
            maxLength={inputLimit}
            autoSize={{ minRows: 1, maxRows: 4 }}
            className={`${styles.input} ${charStatus.isError ? styles.inputError : ''} ${isQuotaReached ? styles.inputDisabled : ''}`}
          />
          <button className={styles.sendButton} onClick={handleSend} disabled={!inputValue.trim() || isLoading || isQuotaReached || charStatus.isError} aria-label={t('aiChat.sendAriaLabel')}>
            <svg className={styles.sendIcon} aria-hidden="true">
              <use xlinkHref="#icon-SEND"></use>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const modalNode = isMobile ? (
    <Drawer
      open={isModalOpen}
      onClose={() => handleClose()}
      placement="bottom"
      height="75%"
      className={styles.aiChatDrawer}
      rootClassName={styles.aiChatDrawer}
      closeIcon={<SvgIcon name="x-black" size={16} />}
      title={
        <div className={styles.drawerTitle}>
          <div className={styles.drawerTitleText}>{t('aiChat.title')}</div>
          <div className={styles.drawerSubtitle}>{getSubtitle()}</div>
        </div>
      }
    >
      {content}
    </Drawer>
  ) : (
    <Modal
      open={isModalOpen}
      onCancel={() => handleClose()}
      footer={null}
      width={isExpanded ? '50vw' : 440}
      closable={false}
      mask={false}
      className={styles.aiChatModal}
      wrapClassName={`${styles.aiChatModalWrap} ${isExpanded ? styles.aiChatModalWrapExpanded : ''}`}
    >
      {content}
    </Modal>
  );

  return (
    <>
      {modalNode}

      {/* 配额限制提示 */}
      <ConfirmModal
        open={showLimitModal}
        icon={isUpgradeVariant ? 'Upgrade' : 'warning'}
        iconBgColor={isUpgradeVariant ? 'var(--yellow-10)' : undefined}
        iconColor={isUpgradeVariant ? 'var(--yellow-100)' : undefined}
        title={limitModalTitle}
        description={isUpgradeVariant ? limitModalUpgradeDesc : limitModalPaidDesc}
        confirmText={isUpgradeVariant ? limitModalUpgradeText : limitModalCloseText}
        cancelText={limitModalCancelText}
        onConfirm={handleLimitConfirm}
        onCancel={handleLimitCancel}
        showCancelButton={isUpgradeVariant}
      />
    </>
  );
};

export default AIChatModal;
